<template>
  <div class="login-banner">
    <div class="login-banner__container">
      <div class="login-banner__text" v-if="!hasEmailVerifyError && !hasLoginError">
        <Aloha class="login-banner__aloha" />
        <h2 class="login-banner__subtitle">{{ invite ? $t('invite.welcome') : $t('login.welcome-back') }}!</h2>
      </div>
      <div :class="{ visible: hasEmailVerifyError }" class="login-banner__error" v-else-if="hasEmailVerifyError">
        {{ emailVerifyError }}
        <span @click="sendEmail" class="login-banner__sendEmail">{{ this.$t('login.resend-email') }}</span>
      </div>
      <div :class="{ visible: hasLoginError }" class="login-banner__error" v-else>
        {{ loginError }}
      </div>
      <div class="login-banner__actions">
        <LocaleProvider class="login-banner__lang">
          <template slot-scope="{ changeLocale, currentLocale }">
            <LocaleToggle :changeLocale="changeLocale" :currentLocale="currentLocale" />
          </template>
        </LocaleProvider>
        <Beacon class="login-banner__beacon" />
      </div>

      <div class="login-banner__mobile-actions" v-closable="hideMobileActions">
        <BaseRoundButton
          @click.native="toggleMobileActions"
          :size="44"
          icon="more-horizontal"
          is-white
          :has-shadow="invite"
        />
        <transition name="dropdown-basic">
          <ul class="login-banner__ul" v-show="mobileActionsVisible" @click="hideMobileActions">
            <li class="login-banner__li" v-on:click.self="onLocaleClick">
              <LocaleProvider class="login-banner__li--lang" ref="localeProvider">
                <template slot-scope="{ changeLocale, currentLocale }">
                  <LocaleToggle :changeLocale="changeLocale" :currentLocale="currentLocale" />
                </template>
              </LocaleProvider>
              {{ $t('login.change-language') }}
            </li>
            <li class="login-banner__li" v-on:click.self="onHelpCenterClick">
              <Beacon noTooltip ref="beacon" />
              {{ $t('navigation-bar.help-center') }}
            </li>
          </ul>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import Aloha from '@/assets/images/aloha-logo.svg';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import Beacon from '@/components/Beacon';
import { LOCALE } from '@/util/i18n/locale';
import LocaleProvider from '@/components/locale/LocaleProvider';
import LocaleToggle from '@/components/locale/LocaleToggle';

import { mapActions, mapGetters } from 'vuex';

export default {
  components: {
    Aloha,
    BaseRoundButton,
    Beacon,
    LocaleProvider,
    LocaleToggle,
  },
  data() {
    return {
      mobileActionsVisible: false,
    };
  },
  props: {
    invite: {
      type: Boolean,
      default: false,
    },
  },
  inject: ['$validator'],
  computed: {
    ...mapGetters('auth', ['error']),
    emailVerifyError() {
      return this.error && this.error.type === 'EMAIL_NOT_VERIFIED'
        ? this.$t('login.email-not-verified-error')
        : undefined;
    },
    loginError() {
      return this.error && this.error.type !== 'EMAIL_NOT_VERIFIED' ? this.$t('login.unknown-error') : undefined;
    },
    hasEmailVerifyError() {
      return this.emailVerifyError !== undefined;
    },
    hasLoginError() {
      return this.loginError !== undefined;
    },
  },
  methods: {
    ...mapActions('sendEmailConfirmation', ['resendEmailFromLogin']),
    sendEmail() {
      const error = this.errors.items.find((x) => x.field === 'email-not-verified');

      this.resendEmailFromLogin(error.email)
        .then(() => {
          this.$toast.success(this.$t('login.resend-email-success'));
        })
        .catch((error) => {
          if (error.type === 'TOKEN_NOT_USED') {
            this.$toast.warning(this.$t('login.resend-email-error'));
          }
        });
    },
    toggleMobileActions() {
      this.mobileActionsVisible = !this.mobileActionsVisible;
    },
    hideMobileActions() {
      this.mobileActionsVisible = false;
    },
    onHelpCenterClick() {
      this.$refs.beacon.toggle();
    },
    onLocaleClick() {
      const nextLocale = this.$refs.localeProvider.currentLocale === LOCALE.FR_CA ? LOCALE.EN_CA : LOCALE.FR_CA;
      this.$refs.localeProvider.changeLocale(nextLocale);
    },
  },
};
</script>

<style lang="scss">
#beacon {
  pointer-events: none;
}

.login-banner {
  position: relative;
  width: 100%;

  &__actions {
    align-items: center;
    display: flex;
    gap: 0;
    position: fixed;
    right: 32px;
    top: 32px;
  }

  &__container {
    align-items: center;
    display: flex;
    flex-direction: row;
  }

  &__text {
    display: flex;
    flex-direction: column;
    padding-left: $space-sm;
  }

  &__aloha {
    color: $color-brand-500;
  }

  &__subtitle {
    color: $color-neutral;
    font-family: $font-family-raleway;
    font-size: 24px;
    font-weight: 400;
    margin: 8px 0 0 0;
  }

  &__error {
    background-color: $color-error;
    border-radius: $border-radius-small;
    color: $color-neutral-pale;
    font-size: 16px;
    margin-left: 20px;
    opacity: 0;
    padding: 16px;
    position: relative;
    transition: opacity 200ms ease;
    visibility: hidden;

    &.visible {
      opacity: 1;
      visibility: visible;
    }

    &::after {
      background-color: $color-error;
      border-radius: $border-radius-small;
      bottom: 0;
      content: ' ';
      display: block;
      height: 10px;
      left: -4.5px;
      margin: auto;
      position: absolute;
      top: 0;
      transform: rotate(-45deg);
      width: 10px;
    }
  }

  &__beacon {
    display: block;
  }

  &__lang {
    display: block;
  }

  &__mobile-actions {
    display: none;
  }

  &__ul {
    background-color: $color-white;
    border-radius: $border-radius-small;
    box-shadow: $shadow-center;
    list-style: none;
    padding: 1rem;
    position: absolute;
    margin: 0;
    right: 8px;
    top: 120%;
    width: 270px;
  }

  &__li {
    height: 52px;
    cursor: pointer;
    display: flex;
    list-style: none;
    padding: 12px 0px;
    align-self: center;
    align-items: center;
    transition: color 200ms ease;

    .feather {
      transition: color 200ms ease;
    }

    &--lang {
      padding: 0px 10.5px;
    }

    &:hover {
      color: $color-accent;

      .feather {
        color: $color-accent;
      }

      .locale-toggle {
        border-color: $color-accent;
        color: $color-accent;
      }
    }
  }

  &__sendEmail {
    display: block;
    text-decoration: underline;
    cursor: pointer;
  }
}

@media screen and (max-width: 1080px) {
  .login-banner {
    &__text {
      padding-left: 16px;
      margin-left: 16px;
    }

    &__subtitle {
      font-size: 20px;
      margin: 0;
      margin-top: 12px;
    }

    &__mobile-actions {
      position: fixed;
      top: 24px;
      right: 32px;
      display: block;
    }

    &__actions {
      display: none;
    }
  }
}
</style>
